import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import { colorFor } from "./palette";

/**
 * Donut with percentage labels, a legend and the total in the centre.
 *
 * Used for retained clients (Churned / Retained / Initial term / New) and
 * client health RAG, laid out the way the board pack shows them: each slice
 * labelled with its count and share, and a legend beside the ring repeating
 * both, so the split reads without hovering.
 */
const RADIAN = Math.PI / 180;

function percentOf(value, total) {
  if (!total) return 0;
  const share = (value / total) * 100;
  // One decimal only where a whole number would hide a small slice.
  return share < 10 && share % 1 !== 0 ? Math.round(share * 10) / 10 : Math.round(share);
}

export default function DonutChart({ groups = [], total, totalLabel = "Total", colors = {}, formatValue, onGroupClick }) {
  const format = formatValue || ((v) => new Intl.NumberFormat("en-GB").format(v));
  const data = groups.map((group, index) => ({
    name: group.label,
    value: group.value,
    count: group.count,
    fill: colorFor(group.label, index, colors),
  }));

  if (!data.length) {
    return <p className="py-12 text-center text-sm text-[#4a5b68]">No records match this report.</p>;
  }

  const sum = data.reduce((acc, d) => acc + d.value, 0);
  const centreTotal = total ?? sum;

  // "32, 12%" beside each slice, on a short leader line.
  const renderLabel = ({ cx, cy, midAngle, outerRadius, value, index }) => {
    const radius = outerRadius + 18;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="#323c44"
        fontSize={12}
        fontWeight={700}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${format(value)}, ${percentOf(value, sum)}%`}
      </text>
    );
  };

  return (
    <div className="flex flex-col items-center gap-6 md:flex-row">
      <div className="relative w-full md:w-3/5">
        <ResponsiveContainer width="100%" height={300} minWidth={260}>
          <PieChart margin={{ top: 16, right: 60, bottom: 16, left: 60 }}>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="52%"
              outerRadius="78%"
              paddingAngle={1}
              stroke="#ffffff"
              strokeWidth={2}
              isAnimationActive={false}
              label={renderLabel}
              labelLine={{ stroke: "#a9b3bb", strokeWidth: 1 }}
              cursor={onGroupClick ? "pointer" : undefined}
              onClick={(entry) => onGroupClick && onGroupClick(entry?.name ?? entry?.payload?.name)}
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ borderRadius: 8, borderColor: "#cccccc", boxShadow: "0 0 20px rgba(0, 0, 0, 0.12)" }}
              formatter={(value, name) => [`${format(value)} (${percentOf(value, sum)}%)`, name]}
            />
          </PieChart>
        </ResponsiveContainer>

        {/* Centre total, pinned over the ring so it stays crisp in the PDF. */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <p className="font-sans text-3xl font-bold leading-none text-[#323c44]">{format(centreTotal)}</p>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#4a5b68]">{totalLabel}</p>
        </div>
      </div>

      {/* Legend with the figures, as in the board pack. */}
      <ul className="w-full space-y-3 md:w-2/5">
        {data.map((entry) => (
          <li key={entry.name}>
            <button
              type="button"
              onClick={() => onGroupClick && onGroupClick(entry.name)}
              className={`grid w-full grid-cols-[auto_1fr_auto_auto] items-center gap-3 text-left text-sm ${
                onGroupClick ? "cursor-pointer hover:opacity-80" : "cursor-default"
              }`}
            >
              <span className="h-3.5 w-3.5 rounded-sm" style={{ backgroundColor: entry.fill }} />
              <span className="text-[#323c44]">{entry.name}</span>
              <span className="font-bold tabular-nums text-[#323c44]">{format(entry.value)}</span>
              <span className="w-16 text-right tabular-nums text-[#7d8b96]">({percentOf(entry.value, sum)}%)</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
